/**
 * Quote — the public quote-request form.
 *
 * Collects name / phone / email / smoker, and one or more coverage selections
 * (a checkbox per type with an amount dropdown). POSTs to /api/quotes, which
 * persists the request and emails Andy. No pricing logic here.
 */

import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Turnstile, { turnstileEnabled } from '../components/Turnstile';
import { pushEvent } from '../lib/analytics';
import api from '../lib/api';
import { COVERAGE } from '../lib/coverage';

const initialRows = Object.fromEntries(
  COVERAGE.map((c) => [c.type, { checked: false, amount: '' }]),
);

// Exact wording shown to the user on the consent checkbox. Sent to the server
// alongside consent=true so each row has the wording in its audit trail. If
// this text changes, ship it as a new release — the server stores whatever the
// client sends per submit, so old rows remain associated with the wording in
// force when they were captured.
//
// SC insurance-compliance / legal review complete (approved 2026-07-16) — this
// wording is cleared for launch. Mirrors the WordPress Contact Form 7 consent
// language (iprotectyou.family, captured 2026-05-29) so the disclosure is
// identical across the marketing site and the app.
const CONSENT_TEXT =
  'By submitting this form, I agree that I Protect You LLC ' +
  '(iProtectYou.Family) and a licensed insurance agent may contact me at the ' +
  'phone number and email address I provided — by phone call, text message ' +
  '(SMS), and email — regarding insurance products and services. I understand ' +
  'these calls and texts may be made using automated dialing technology or a ' +
  'prerecorded or artificial voice, and that consent is not a condition of ' +
  'purchasing any product or service. Message frequency may vary, and message ' +
  'and data rates may apply. Reply STOP to unsubscribe from texts or HELP for ' +
  'help. See our Privacy Policy and Terms of Service.';

export default function Quote() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState(''); // '' | 'male' | 'female'
  const [smoker, setSmoker] = useState(''); // '' | 'yes' | 'no'
  const [rows, setRows] = useState(initialRows);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // Anti-spam: honeypot field (humans never see it), time-to-submit, and the
  // Turnstile token (widget renders only when the site key is configured).
  const [website, setWebsite] = useState('');
  const [tsToken, setTsToken] = useState('');
  const tsRef = useRef(null);
  const formStartRef = useRef(Date.now());

  const toggle = (type) =>
    setRows((r) => ({
      ...r,
      [type]: { ...r[type], checked: !r[type].checked },
    }));

  const setAmount = (type, amount) =>
    setRows((r) => ({ ...r, [type]: { ...r[type], amount } }));

  const selections = COVERAGE.filter((c) => rows[c.type].checked).map((c) => ({
    type: c.type,
    amount: rows[c.type].amount,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('Please fill in your name, email, and phone.');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (
      /[A-Za-z]/.test(phone) ||
      !(
        phoneDigits.length === 10 ||
        (phoneDigits.length === 11 && phoneDigits.startsWith('1'))
      )
    ) {
      setError('Please enter a valid 10-digit US phone number.');
      return;
    }
    const ageNum = Number(age);
    if (!age.trim() || !Number.isInteger(ageNum) || ageNum < 0 || ageNum > 120) {
      setError('Please enter a valid age (0–120).');
      return;
    }
    if (sex !== 'male' && sex !== 'female') {
      setError('Please select your sex.');
      return;
    }
    if (smoker !== 'yes' && smoker !== 'no') {
      setError('Please tell us whether you use tobacco/nicotine.');
      return;
    }
    if (selections.length === 0) {
      setError('Please choose at least one type of coverage.');
      return;
    }
    if (selections.some((s) => !s.amount)) {
      setError('Please pick an amount for each coverage type you selected.');
      return;
    }
    if (!consent) {
      setError('Please agree to the contact terms before submitting.');
      return;
    }
    if (turnstileEnabled() && !tsToken) {
      setError(
        'Please complete the verification above, or wait a moment for it to finish.',
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/quotes', {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        age: ageNum,
        sex,
        smoker: smoker === 'yes',
        selections,
        consent: true,
        consent_text: CONSENT_TEXT,
        website,
        elapsed_ms: Date.now() - formStartRef.current,
        turnstile_token: tsToken || undefined,
      });
      pushEvent('generate_lead_quote', {
        lead_type: 'quote_request',
        coverage_types: selections.map((s) => s.type),
      });
      setDone(true);
    } catch (err) {
      // Turnstile tokens are single-use — mint a fresh one for the retry.
      tsRef.current?.reset();
      setError(
        err?.response?.data?.error ||
          'Something went wrong sending your request. Please try again or call Andy.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Helmet>
          <title>Request received — iProtectYou.Family</title>
        </Helmet>
        <main style={wrap}>
          <h1 style={{ color: 'var(--color-navy)' }}>Thank you!</h1>
          <p style={{ lineHeight: 1.6 }}>
            Andy got your request and will reach out shortly. If you need him
            sooner, call or text <a href="tel:+18668792586">1-866-879-2586</a>.
          </p>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Get a Free Quote — iProtectYou.Family</title>
      </Helmet>

      <main style={wrap}>
        <h1 style={{ margin: '0 0 0.25rem', color: 'var(--color-navy)' }}>
          Get Your Free Quote
        </h1>
        <p style={{ color: 'rgba(0,0,0,0.6)', marginTop: 0 }}>
          Tell us a little about what you're looking for and Andy will follow up
          — no obligation.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Honeypot — hidden from humans (and screen readers); bots that
              auto-fill every field give themselves away. */}
          <div style={honeypotWrap} aria-hidden="true">
            <label htmlFor="q-website">Website</label>
            <input
              id="q-website"
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <label style={lbl} htmlFor="q-name">
            Full name
          </label>
          <input
            id="q-name"
            style={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            disabled={submitting}
          />

          <label style={lbl} htmlFor="q-phone">
            Phone
          </label>
          <input
            id="q-phone"
            style={input}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            disabled={submitting}
          />

          <label style={lbl} htmlFor="q-email">
            Email
          </label>
          <input
            id="q-email"
            style={input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            disabled={submitting}
          />

          <label style={lbl} htmlFor="q-age">
            Age
          </label>
          <input
            id="q-age"
            style={input}
            type="number"
            min="0"
            max="120"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            disabled={submitting}
          />

          <fieldset style={fieldset}>
            <legend style={legend}>Sex</legend>
            <label style={radioLbl}>
              <input
                type="radio"
                name="sex"
                checked={sex === 'male'}
                onChange={() => setSex('male')}
                disabled={submitting}
              />{' '}
              Male
            </label>
            <label style={radioLbl}>
              <input
                type="radio"
                name="sex"
                checked={sex === 'female'}
                onChange={() => setSex('female')}
                disabled={submitting}
              />{' '}
              Female
            </label>
          </fieldset>

          <fieldset style={fieldset}>
            <legend style={legend}>Do you use tobacco or nicotine?</legend>
            <label style={radioLbl}>
              <input
                type="radio"
                name="smoker"
                checked={smoker === 'no'}
                onChange={() => setSmoker('no')}
                disabled={submitting}
              />{' '}
              No
            </label>
            <label style={radioLbl}>
              <input
                type="radio"
                name="smoker"
                checked={smoker === 'yes'}
                onChange={() => setSmoker('yes')}
                disabled={submitting}
              />{' '}
              Yes
            </label>
          </fieldset>

          <fieldset style={fieldset}>
            <legend style={legend}>
              What coverage are you interested in?
            </legend>
            {COVERAGE.map((c) => {
              const row = rows[c.type];
              return (
                <div key={c.type} style={covRow}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={() => toggle(c.type)}
                      disabled={submitting}
                    />
                    {c.label}
                  </label>
                  <select
                    aria-label={`${c.label} amount`}
                    style={{ ...select, opacity: row.checked ? 1 : 0.5 }}
                    value={row.amount}
                    onChange={(e) => setAmount(c.type, e.target.value)}
                    disabled={!row.checked || submitting}
                  >
                    <option value="">Amount…</option>
                    {c.amounts.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </fieldset>

          <label style={consentLbl}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={submitting}
              aria-required="true"
              data-testid="quote-consent"
            />
            <span>
              By submitting this form, I agree that I Protect You LLC
              (iProtectYou.Family) and a licensed insurance agent may contact me
              at the phone number and email address I provided — by phone call,
              text message (SMS), and email — regarding insurance products and
              services. I understand these calls and texts may be made using
              automated dialing technology or a prerecorded or artificial voice,
              and that consent is not a condition of purchasing any product or
              service. Message frequency may vary, and message and data rates
              may apply. Reply STOP to unsubscribe from texts or HELP for help.
              See our{' '}
              <a
                href="https://iprotectyou.family/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://iprotectyou.family/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              .
            </span>
          </label>

          <Turnstile ref={tsRef} onToken={setTsToken} />

          {error ? (
            <div role="alert" style={errorBox} data-testid="quote-error">
              {error}
            </div>
          ) : null}

          <button type="submit" style={submitBtn} disabled={submitting}>
            {submitting ? 'Sending…' : 'Request My Quote'}
          </button>
        </form>
      </main>
    </>
  );
}

const wrap = { maxWidth: 560, margin: '0 auto', padding: '2.5rem 1.5rem' };
// Off-screen (not display:none — some bots skip invisible fields).
const honeypotWrap = {
  position: 'absolute',
  left: '-9999px',
  top: 'auto',
  width: 1,
  height: 1,
  overflow: 'hidden',
};
const lbl = {
  display: 'block',
  fontWeight: 600,
  margin: '1rem 0 0.35rem',
};
const input = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  fontSize: '1rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 8,
  boxSizing: 'border-box',
};
const fieldset = {
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 8,
  margin: '1.25rem 0 0',
  padding: '0.75rem 1rem 1rem',
};
const legend = { fontWeight: 600, padding: '0 0.35rem' };
const radioLbl = { display: 'inline-flex', alignItems: 'center', marginRight: '1.5rem' };
const covRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '0.4rem 0',
};
const select = {
  padding: '0.5rem 0.6rem',
  fontSize: '0.95rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 8,
  minWidth: 120,
};
const errorBox = {
  marginTop: '1rem',
  padding: '0.65rem 0.85rem',
  background: '#FEF2F2',
  color: '#9F1239',
  borderRadius: 8,
  fontSize: '0.92rem',
};
const submitBtn = {
  marginTop: '1.5rem',
  width: '100%',
  padding: '0.85rem 1rem',
  background: 'var(--color-navy)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '1rem',
  fontWeight: 700,
  cursor: 'pointer',
};
const consentLbl = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  margin: '1.5rem 0 0',
  fontSize: '0.88rem',
  lineHeight: 1.45,
  color: 'rgba(0,0,0,0.7)',
};
